import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  alpha,
  useTheme,
  InputBase,
  Divider,
  Fade,
  Zoom,
  Grow,
  Badge,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Search as SearchIcon,
  ClearAll as ClearAllIcon,
  FilterList as FilterListIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  MoreHoriz as MoreIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ViewList as ViewListIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ResponsibleForm from '../components/ResponsibleForm';

// Função para gerar as iniciais do nome
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Função para definir uma cor baseada no nome
const stringToColor = (string) => {
  if (!string) return '#888';
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xFF;
    color += ('00' + value.toString(16)).substr(-2);
  }
  return color;
};

const Responsibles = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [responsibles, setResponsibles] = useState([]);
  const [filteredResponsibles, setFilteredResponsibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [openEditForm, setOpenEditForm] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [associatedProjects, setAssociatedProjects] = useState([]);
  const navigate = useNavigate();

  // Verificar autenticação e buscar dados
  useEffect(() => {
    fetchResponsibles();
  }, []);

  // Filtrar responsáveis quando o termo de pesquisa mudar
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredResponsibles(responsibles);
    } else {
      const filtered = responsibles.filter(responsible => {
        const searchLower = searchTerm.toLowerCase();
        return (
          responsible.full_name.toLowerCase().includes(searchLower) ||
          (responsible.department && responsible.department.toLowerCase().includes(searchLower)) ||
          (responsible.position && responsible.position.toLowerCase().includes(searchLower)) ||
          (responsible.email && responsible.email.toLowerCase().includes(searchLower))
        );
      });
      setFilteredResponsibles(filtered);
    }
  }, [searchTerm, responsibles]);

  const fetchResponsibles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/technical-responsibles/');
      setResponsibles(response.data);
      setFilteredResponsibles(response.data);
    } catch (error) {
      console.error('Erro ao buscar responsáveis técnicos:', error);
      if (error.response?.status === 401) {
        navigate('/login');
        return;
      } else if (error.response?.status === 403) {
        setNotification({
          open: true,
          message: 'Você não tem permissão para acessar esta página',
          severity: 'error'
        });
        setTimeout(() => navigate('/projects'), 2000);
        return;
      }
      setNotification({
        open: true,
        message: 'Erro ao carregar responsáveis técnicos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResponsible = async (responsibleData) => {
    try {
      await api.post('/technical-responsibles/', responsibleData);
      setOpenCreateForm(false);
      setNotification({
        open: true,
        message: 'Responsável técnico criado com sucesso!',
        severity: 'success'
      });
      fetchResponsibles();
    } catch (error) {
      console.error('Erro ao criar responsável técnico:', error);
      setNotification({
        open: true,
        message: `Erro ao criar responsável técnico: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleEditResponsible = async (responsibleData) => {
    try {
      await api.put(`/technical-responsibles/${selectedResponsible.responsible_id}`, responsibleData);
      setOpenEditForm(false);
      setNotification({
        open: true,
        message: 'Responsável técnico atualizado com sucesso!',
        severity: 'success'
      });
      fetchResponsibles();
    } catch (error) {
      console.error('Erro ao atualizar responsável técnico:', error);
      setNotification({
        open: true,
        message: `Erro ao atualizar responsável técnico: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDeleteResponsible = async () => {
    try {
      // Já verificamos se há projetos associados ao abrir o diálogo
      if (associatedProjects.length > 0) {
        setOpenDeleteDialog(false);
        setNotification({
          open: true,
          message: `Não é possível excluir o responsável "${selectedResponsible.full_name}" pois está associado a ${associatedProjects.length} projeto(s).`,
          severity: 'error'
        });
        return;
      }
      
      // Se não houver projetos associados, prossegue com a exclusão
      await api.delete(`/technical-responsibles/${selectedResponsible.responsible_id}`);
      setOpenDeleteDialog(false);
      setNotification({
        open: true,
        message: 'Responsável técnico excluído com sucesso!',
        severity: 'success'
      });
      fetchResponsibles();
    } catch (error) {
      console.error('Erro ao excluir responsável técnico:', error);
      setNotification({
        open: true,
        message: `Erro ao excluir responsável técnico: ${error.response?.data?.detail || error.message}`,
        severity: 'error'
      });
      setOpenDeleteDialog(false);
    }
  };

  const handleOpenEditForm = (responsible, event) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedResponsible(responsible);
    setOpenEditForm(true);
  };

  const handleOpenDeleteDialog = (responsible) => {
    setSelectedResponsible(responsible);
    setOpenDeleteDialog(true);
    
    // Verificar se há projetos associados
    const checkProjects = async () => {
      try {
        const projectsResponse = await api.get('/projects/');
        const projects = projectsResponse.data;
        
        // Filtra projetos onde o responsável é o selecionado
        const responsibleProjects = projects.filter(
          project => project.responsible === responsible.full_name
        );
        
        // Armazenar projetos associados no estado
        setAssociatedProjects(responsibleProjects);
      } catch (error) {
        console.error('Erro ao verificar projetos associados:', error);
      }
    };
    
    checkProjects();
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setAssociatedProjects([]);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const ResponsibleGridView = () => (
    <Box sx={{ mt: 6 }}>
      <Grid container spacing={3}>
        {filteredResponsibles.map((responsible) => (
          <Grow key={responsible.responsible_id} in timeout={300}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                  position: 'relative',
                  overflow: 'visible',
                  mt: 5
                }}
                onClick={() => navigate(`/responsibles/${responsible.responsible_id}`)}
              >
                <CardContent
                  sx={{
                    pt: 7,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  {responsible.profile_image_url ? (
                    <Avatar
                      src={responsible.profile_image_url}
                      alt={responsible.full_name}
                      sx={{
                        width: 84,
                        height: 84,
                        position: 'absolute',
                        top: -42,
                        border: (theme) => `4px solid ${theme.palette.background.paper}`,
                        boxShadow: 2,
                        bgcolor: (theme) => theme.palette.primary.main,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 84,
                        height: 84,
                        position: 'absolute',
                        top: -42,
                        border: (theme) => `4px solid ${theme.palette.background.paper}`,
                        boxShadow: 2,
                        bgcolor: (theme) => theme.palette.primary.main,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      {getInitials(responsible.full_name)}
                    </Avatar>
                  )}
                  
                  <Box 
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      pt: 3,
                      pb: 2,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography 
                      variant="h6" 
                      component="h2"
                      align="center"
                      sx={{ 
                        fontWeight: 'bold',
                        px: 2,
                        cursor: 'pointer',
                        '&:hover': {
                          color: theme.palette.primary.main
                        }
                      }}
                      onClick={() => navigate(`/responsibles/${responsible.responsible_id}`)}
                    >
                      {responsible.full_name}
        </Typography>
                    
                    {responsible.position && (
                      <Chip 
                        label={responsible.position} 
                        size="small" 
                        color="primary"
            variant="outlined"
                        sx={{ mt: 1 }}
                      />
                    )}
      </Box>

                  <CardContent sx={{ p: 2, flexGrow: 1 }}>
                    {responsible.department && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {responsible.department}
                        </Typography>
                      </Box>
                    )}
                    
                    {responsible.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {responsible.email}
                        </Typography>
        </Box>
                    )}
                    
                    {responsible.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {responsible.phone}
          </Typography>
                      </Box>
                    )}
                  </CardContent>
                  
                  <CardActions 
                    sx={{ 
                      p: 2,
                      pt: 0,
                      bgcolor: alpha(theme.palette.background.paper, 0.5),
                      borderTop: `1px solid ${theme.palette.divider}`,
                      justifyContent: 'flex-end',
                      transition: 'all 0.3s ease',
                      ...(isMobile ? {} : {
                        opacity: 0.6,
                        transform: 'translateY(10px)'
                      })
                    }}
                  >
                    <Tooltip title="Editar">
                      <IconButton 
                        size="small" 
                        onClick={(event) => handleOpenEditForm(responsible, event)}
            color="primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton 
                        size="small" 
                        onClick={(event) => {
                          event.stopPropagation();
                          handleOpenDeleteDialog(responsible);
                        }}
                        color="error"
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </CardContent>
              </Card>
            </Grid>
          </Grow>
        ))}
      </Grid>
    </Box>
  );

  const ResponsibleListView = () => (
    <TableContainer component={Paper} sx={{ mb: 4, borderRadius: '16px', overflow: 'hidden' }}>
          <Table>
            <TableHead>
          <TableRow sx={{ 
            bgcolor: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.primary.main, 0.15)
              : alpha(theme.palette.primary.main, 0.05)
          }}>
                <TableCell>ID</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Departamento</TableCell>
                <TableCell>Cargo</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
          {filteredResponsibles.map((responsible, index) => (
            <Fade key={responsible.responsible_id} in timeout={300 + (index * 50)}>
              <TableRow 
                sx={{ 
                  '&:nth-of-type(odd)': {
                    bgcolor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.action.hover, 0.1)
                      : alpha(theme.palette.action.hover, 0.05),
                  },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.action.hover, 0.2)
                      : alpha(theme.palette.action.hover, 0.1),
                  }
                }}
              >
                  <TableCell>{responsible.responsible_id}</TableCell>
                  <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {responsible.profile_image_url ? (
                      <Avatar
                        src={responsible.profile_image_url}
                        alt={responsible.full_name}
                        sx={{ mr: 2, width: 40, height: 40 }}
                      />
                    ) : (
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        {getInitials(responsible.full_name)}
                      </Avatar>
                    )}
                    <Button
                      color="primary"
                      sx={{ 
                        textTransform: 'none', 
                        fontWeight: 'bold',
                        justifyContent: 'flex-start',
                        padding: '0'
                      }}
                      onClick={() => navigate(`/responsibles/${responsible.responsible_id}`)}
                    >
                      {responsible.full_name}
                    </Button>
                  </Box>
                  </TableCell>
                  <TableCell>{responsible.department || '-'}</TableCell>
                  <TableCell>{responsible.position || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {responsible.email && (
                        <Tooltip title={responsible.email}>
                          <IconButton size="small" color="primary">
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {responsible.phone && (
                        <Tooltip title={responsible.phone}>
                          <IconButton size="small" color="primary">
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!responsible.email && !responsible.phone && '-'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={responsible.is_active ? 'Ativo' : 'Inativo'}
                      color={responsible.is_active ? 'success' : 'default'}
                      size="small"
                    icon={responsible.is_active ? <CheckIcon fontSize="small" /> : <CloseIcon fontSize="small" />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton
                      size="small" 
                      onClick={(event) => handleOpenEditForm(responsible, event)}
                        color="primary"
                      >
                      <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                      size="small" 
                      onClick={(event) => {
                        event.stopPropagation();
                        handleOpenDeleteDialog(responsible);
                      }}
                        color="error"
                      >
                      <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
            </Fade>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
  );

  return (
    <Container maxWidth="xl">
      <Fade in timeout={300}>
        <Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'stretch', sm: 'center' }, 
            mb: 4, 
            gap: 2
          }}>
            <Typography variant="h4" component="h1">
              Responsáveis Técnicos
            </Typography>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={fetchResponsibles}
                startIcon={<RefreshIcon />}
                sx={{ 
                  borderRadius: '8px'
                }}
              >
                Atualizar
              </Button>
              <Zoom in timeout={500}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenCreateForm(true)}
                  startIcon={<AddIcon />}
                  sx={{ 
                    borderRadius: '8px', 
                    px: 2
                  }}
                >
                  Novo Responsável
                </Button>
              </Zoom>
            </Box>
          </Box>

          <Paper
            elevation={2}
            sx={{
              p: 2,
              mb: 4,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: 2,
              borderRadius: '12px'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              bgcolor: alpha(theme.palette.common.black, 0.05),
              px: 2,
              borderRadius: '8px',
              flex: 1,
              maxWidth: { sm: '400px' }
            }}>
              <SearchIcon color="action" sx={{ mr: 1 }} />
              <InputBase
                placeholder="Buscar responsável..."
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ py: 1 }}
                inputProps={{ 'aria-label': 'buscar responsável' }}
              />
              {searchTerm && (
                <IconButton size="small" onClick={clearSearch} aria-label="limpar busca">
                  <ClearAllIcon fontSize="small" />
                </IconButton>
              )}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              ml: 'auto',
              borderRadius: '8px',
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }}>
              <Tooltip title="Visualização em grade">
                <IconButton 
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                  onClick={() => setViewMode('grid')}
                  sx={{ 
                    borderRadius: 0,
                    bgcolor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <DashboardIcon />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              <Tooltip title="Visualização em lista">
                <IconButton 
                  color={viewMode === 'list' ? 'primary' : 'default'}
                  onClick={() => setViewMode('list')}
                  sx={{ 
                    borderRadius: 0,
                    bgcolor: viewMode === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : filteredResponsibles.length === 0 ? (
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center',
                borderRadius: '16px',
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.8)
                  : alpha(theme.palette.grey[50], 0.8),
                border: `1px dashed ${theme.palette.divider}`
              }}
            >
              {searchTerm ? (
                <>
                  <Typography variant="h6" gutterBottom>Nenhum resultado encontrado</Typography>
                  <Typography variant="body1" color="textSecondary" mb={3}>
                    Não encontramos nenhum responsável técnico que corresponda à sua busca
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={clearSearch}
                    startIcon={<ClearAllIcon />}
                  >
                    Limpar Busca
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="h6" gutterBottom>Nenhum responsável técnico encontrado</Typography>
                  <Typography variant="body1" color="textSecondary" mb={3}>
                    Clique em "Novo Responsável" para adicionar um responsável técnico
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenCreateForm(true)}
                    startIcon={<AddIcon />}
                  >
                    Novo Responsável
                  </Button>
                </>
              )}
            </Paper>
          ) : (
            <>
              {viewMode === 'grid' ? <ResponsibleGridView /> : <ResponsibleListView />}
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Typography 
                  variant="body2" 
                  color="textSecondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  Total: <Chip 
                    label={filteredResponsibles.length} 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                  />
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Fade>

      {/* Formulário de criação */}
      <ResponsibleForm
        open={openCreateForm}
        onClose={() => setOpenCreateForm(false)}
        onSubmit={handleCreateResponsible}
      />

      {/* Formulário de edição */}
        <ResponsibleForm
          open={openEditForm}
          onClose={() => setOpenEditForm(false)}
          onSubmit={handleEditResponsible}
          initialData={selectedResponsible}
        isEditing
        />

      {/* Diálogo de confirmação para exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir o responsável técnico{' '}
            <strong>{selectedResponsible?.full_name}</strong>?
            Esta ação não pode ser desfeita.
          </DialogContentText>
          
          {associatedProjects.length > 0 ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Este responsável está associado a {associatedProjects.length} projeto(s) e não pode ser excluído:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {associatedProjects.map(project => (
                  <li key={project.project_id}>
                    <Typography variant="body2">{project.project_name}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              A exclusão não será permitida se o responsável estiver associado a algum projeto ativo.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDeleteDialog} color="primary" variant="outlined">
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteResponsible} 
            color="error" 
            variant="contained" 
            startIcon={<DeleteIcon />}
            disabled={associatedProjects.length > 0}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificações */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Responsibles; 